import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { AuthService } from './auth.service';

/**
 * Adds the bearer token to outgoing requests and, on 401, attempts a single
 * silent refresh + retry. If the refresh fails, the user is logged out.
 *
 * Skips the auth endpoints themselves to avoid refresh loops.
 *
 * IMPORTANT — the `isAuthEndpoint(req.url)` check runs BEFORE
 * `inject(AuthService)` to avoid a circular dependency. The AuthService
 * constructor itself fires `POST /refresh-token` which traverses this
 * interceptor; if we injected AuthService unconditionally, that inject
 * would happen while AuthService is still being instantiated → NG0200.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (isAuthEndpoint(req.url)) {
    return next(req);
  }

  const auth = inject(AuthService);
  const token = auth.accessToken();
  const authed = token ? withBearer(req, token) : req;

  return next(authed).pipe(
    catchError((err: unknown) => {
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        token // only attempt refresh if we had a token to begin with
      ) {
        return auth.refresh().pipe(
          switchMap(() => {
            const fresh = auth.accessToken();
            return next(fresh ? withBearer(req, fresh) : req);
          }),
          catchError(() => {
            auth.logout();
            return throwError(() => err);
          }),
        );
      }
      return throwError(() => err);
    }),
  );
};

function withBearer<T>(req: HttpRequest<T>, token: string): HttpRequest<T> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

function isAuthEndpoint(url: string): boolean {
  return (
    url.includes('/api/auth/login') ||
    url.includes('/api/auth/refresh-token') ||
    url.includes('/api/auth/logout') ||
    url.includes('/api/auth/forgotten-password')
  );
}
